import React, { useLayoutEffect, useRef, useState } from 'react'
import { ILanguageContent, IMenuItem, ISearchContent } from '../../models'
import MenuCard from './MenuCard'
import MenuLang from './MenuLang'
import MenuLinkList from './MenuLinkList'
import MenuSearch from './MenuSearch'
import SubNavigationDesk from './SubNavigationDesk'

function MenuDesktop({
  menuItems,
  languageItems,
  searchContent,
}: {
  menuItems: IMenuItem[]
  languageItems: ILanguageContent[]
  searchContent: ISearchContent
}) {
  const [modalLeftPosition, setModalLeftPosition] = useState(0)
  const modalRef = useRef<HTMLInputElement>(null)
  const [selectedItem, setSelectedItem] = useState<IMenuItem | null>(null)
  const [menuItemRect, setMenuItemRect] = useState({
    width: 0,
    left: 0,
    right: 0,
  })

  const handleMouseEnter = (item: IMenuItem, { left, right, width }) => {
    setSelectedItem(item)

    setMenuItemRect({ width, right, left })
  }

  const handleMouseLeave = () => {
    setSelectedItem(null)
  }

  useLayoutEffect(() => {
    if (modalRef.current) {
      const modalRect = modalRef.current.getBoundingClientRect()
      const contentSize = window.innerWidth > 1440 ? 1440 : window.innerWidth

      const margin =
        window.innerWidth > 1440 ? (window.innerWidth - 1440) / 2 : 0

      const menuItemRightRef = window.innerWidth - menuItemRect.right - margin

      const modalSize = modalRect.width / 2

      const modalLeft =
        menuItemRect.left - modalSize + menuItemRect.width / 2 - margin

      if (menuItemRightRef < modalSize) {
        setModalLeftPosition(contentSize - modalRect.width - 25)
      } else if (modalLeft < 0) {
        setModalLeftPosition(10)
      } else {
        setModalLeftPosition(modalLeft)
      }
    }
  }, [selectedItem])

  const onMenuClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    IsDirectLink: boolean,
    url: string
  ) => {
    event.preventDefault()
    if (IsDirectLink) {
      window.open(url, '_self')
    }
  }

  return (
    <div className="flex gap-9 items-center">
      <div className="flex flex-row gap-8 items-center">
        {menuItems.map((menuItem, index) => (
          <div key={index}>
            <button
              key={index}
              className={`link font-normal ${
                menuItem.IsDirectLink
                  ? menuItem.Highlight
                    ? 'link-menu-highlight'
                    : 'link-text-menu'
                  : 'link-chevron-down'
              }`}
              onClick={(e) =>
                onMenuClick(e, menuItem.IsDirectLink, menuItem.LinkUrl)
              }
              onMouseEnter={(event) =>
                handleMouseEnter(
                  menuItem,
                  (event.target as HTMLElement).getBoundingClientRect()
                )
              }
            >
              {menuItem.MenuTitle}
            </button>
            <div
              className={`${
                selectedItem === menuItem ? ' opacity-100' : 'opacity-0'
              } transition-opacity  duration-500`}
            >
              {selectedItem === menuItem && !selectedItem.IsDirectLink && (
                <div
                  className="z-50 absolute min-h-96 bg-white px-7 flex gap-[30px] flex-row top-[85px] max-w-[95vw] "
                  onMouseLeave={() => handleMouseLeave()}
                  ref={modalRef}
                  style={{
                    left: modalLeftPosition,
                    boxShadow: '0px 5px 20px 0px rgba(0, 0, 0, 0.25)',
                  }}
                >
                  {selectedItem.LeftSide && (
                    <div
                      className={`flex flex-row gap-[30px] pt-7 shrink-0 pr-[30px] ${
                        selectedItem.RightSide
                          ? 'border-r-[1px]  border-separators-grey'
                          : ''
                      }`}
                    >
                      {(selectedItem.LeftSide.Heading ||
                        selectedItem.LeftSide.Text) && (
                        <MenuCard
                          heading={selectedItem.LeftSide.Heading}
                          text={selectedItem.LeftSide.Text}
                          imageUrl={selectedItem.LeftSide.ImageUrl}
                          link={selectedItem.LeftSide.Link}
                        />
                      )}
                      {selectedItem.LeftSide.SubLinks.length > 0 && (
                        <MenuLinkList
                          subLinks={selectedItem.LeftSide.SubLinks}
                        />
                      )}
                    </div>
                  )}
                  {selectedItem.RightSide && (
                    <div className="flex flex-col my-7 gap-[30px] w-min">
                      <div className="flex flex-row gap-[30px]">
                        {selectedItem.RightSide.map((element) => {
                          if ('Heading' in element) {
                            return (
                              <MenuCard
                                heading={element.Heading}
                                text={element.Teaser}
                                imageUrl={element.ImageUrl}
                                link={element.Link}
                                key={element.Heading}
                                linkText={false}
                              />
                            )
                          }
                          if (
                            'LinkList' in element &&
                            element.LinkList?.length > 0
                          ) {
                            return (
                              <div className="w-max">
                                <MenuLinkList subLinks={element.LinkList} />
                              </div>
                            )
                          }
                          if ('Tabs' in element && element.Tabs?.length > 0) {
                            return (
                              <div className="w-max">
                                <SubNavigationDesk tabs={element.Tabs} />
                              </div>
                            )
                          }
                        })}
                      </div>
                      {selectedItem.InfoBox && (
                        <div className="flex flex-col bg-engineering-grey-4 py-4 px-5 gap-1">
                          <div className="w-[402px] ">
                            <p>
                              <b>{selectedItem.InfoBox.Heading}</b>
                            </p>

                            <p className="py-1 text-sm">
                              {selectedItem.InfoBox.Teaser}
                            </p>
                          </div>
                          <div className="flex py-[5px]">
                            <a
                              className="link link-text "
                              href={selectedItem.InfoBox.Link.Url}
                              target={selectedItem.InfoBox.Link.Target}
                            >
                              {selectedItem.InfoBox.Link.Name}
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <MenuSearch searchContent={searchContent} languageItems={languageItems} />
      <MenuLang languageItems={languageItems} />
    </div>
  )
}

export default MenuDesktop
